import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { listIncidents, manualOpenIncident, manualCloseIncident } from "../api"
import IncidentDetail from "./IncidentDetail"

const statusColor = {
  open: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  resolved: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
}

const severityLabel = { info: "Info", minor: "Minor", major: "Major", critical: "Critical" }

const IncidentList = ({ projectId, componentId }) => {
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading] = useState(false)
  const [viewing, setViewing] = useState(null)
  const [statusFilter, setStatusFilter] = useState("open")
  const [showNewForm, setShowNewForm] = useState(false)
  const [newReason, setNewReason] = useState("")
  const [newSeverity, setNewSeverity] = useState("minor")
  const [err, setErr] = useState(null)

  const refresh = async () => {
    setLoading(true)
    const r = await listIncidents(projectId, componentId || null, statusFilter || null)
    setIncidents(r?.incidents || [])
    setLoading(false)
  }

  useEffect(() => { refresh() }, [projectId, componentId, statusFilter])

  const onOpenIncident = async () => {
    if (!componentId) return setErr("Select a component first")
    setErr(null)
    const r = await manualOpenIncident(componentId, newReason, newSeverity)
    if (r?.error) return setErr(r.error)
    setShowNewForm(false)
    setNewReason("")
    refresh()
  }

  const onClose = async (id) => {
    if (!confirm("Close this incident?")) return
    const r = await manualCloseIncident(id)
    if (r?.error) return setErr(r.error)
    refresh()
  }

  if (viewing) return <IncidentDetail incidentId={viewing} onBack={() => { setViewing(null); refresh() }} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography variant="admin_h3">Incidents</Typography>
        <div className="flex gap-2">
          <DropdownSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(v?.[0]?.value || v?.value || v || "")}
            options={[
              { value: "open", label: "Open" },
              { value: "resolved", label: "Resolved" },
              { value: "", label: "All" },
            ]}
          />
          {componentId && (
            <Button variant="secondary" onClick={() => setShowNewForm(!showNewForm)}>
              + New Incident
            </Button>
          )}
        </div>
      </div>

      {showNewForm && (
        <div className="p-3 border border-bordered rounded bg-paper space-y-2">
          <input className="w-full p-2 border border-bordered rounded bg-canvas text-sm" placeholder="Reason..." value={newReason} onChange={(e) => setNewReason(e.target.value)} />
          <div className="flex gap-2 items-center">
            <DropdownSelect value={newSeverity} onChange={(v) => setNewSeverity(v?.[0]?.value || v?.value || v || "minor")} options={Object.entries(severityLabel).map(([v, l]) => ({ value: v, label: l }))} />
            <Button onClick={onOpenIncident}>Open</Button>
            <Button variant="secondary" onClick={() => setShowNewForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {err && <div className="text-error text-sm">Error: {err}</div>}

      {loading && <div className="text-text_secondary">Loading...</div>}
      {!loading && incidents.length === 0 && <div className="text-text_secondary">No incidents found.</div>}

      <div className="grid gap-2">
        {incidents.map((i) => (
          <div key={i._id} className="p-3 rounded-lg border border-bordered bg-paper cursor-pointer hover:shadow-sm" onClick={() => setViewing(i._id)}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{i.reason || "Incident"}</div>
                <div className="text-xs text-text_secondary mt-0.5">
                  {new Date(i.openedAt).toLocaleString()} · {severityLabel[i.severity] || i.severity} · {i.trigger === "auto" ? "Auto" : "Manual"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusColor[i.status] || ""}`}>
                  {i.status}
                </span>
                {i.status === "open" && (
                  <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); onClose(i._id) }}>Close</Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default IncidentList
